/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */
/**
 * ============================================================================
 * FIN SELECTOR V5 - CORE MODULE
 * ============================================================================
 * Shared logic for Modal (G) and Full Page (H) versions
 *
 * @version 5.0.0
 * @updated 2025-12-31
 * @author Clément Morandière - Senior Ecommerce Consultant - Waterspot Experts
 *
 * Features:
 * - State management for wizard steps
 * - Sizing formula: board_width/2 + discipline adjustment + sail influence
 * - Tolerance range: +/- 3cm
 * - Product filtering by discipline, box type, antiweed, size range
 * - Compare functionality (max 3 products)
 * - Event-driven architecture for UI updates
 *
 * No external dependencies required.
 * ============================================================================
 */

(function (global) {
  "use strict";

  // ============================================================================
  // CONSTANTS & CONFIGURATION
  // ============================================================================

  /**
   * Size tolerance in cm (+/- from recommended size)
   * @constant {number}
   */
  const SIZE_TOLERANCE = 3;

  /**
   * Maximum products for comparison
   * @constant {number}
   */
  const MAX_COMPARE_ITEMS = 3;

  /**
   * Product handles to exclude from fin selector (board accessories, adapters, banners, etc.)
   * @constant {Array<string>}
   */
  const EXCLUDED_PRODUCT_HANDLES = [
    "inflatable-boards-toolless-us-box-screw-plate",
    "adapter-tiga-box",
    "adapter-power-box",
    "adapter-trim-box",
    "adapter-tuttle-box",
    "select-banner-200-x-80-technology-inside",
  ];

  /**
   * Discipline offsets for standard sizing formula
   * Applied to board_width/2 calculation
   * @constant {Object}
   */
  const DISCIPLINE_OFFSETS = {
    slalom: 3,
    freerace: 2,
    speed: 0,
    freeride: 1,
    wave: 0,
    freestyle: 0,
  };

  /**
   * Wave sizing: Volume × Weight matrix (from fin-selector-rules.json)
   * Returns range string like "24-26" for single fin
   * @constant {Object}
   */
  const WAVE_VOLUME_MATRIX = {
    light: {
      // < 75kg
      "80-89": { single: [22, 24], quad: [14, 16] },
      "90-99": { single: [24, 26], quad: [16, 18] },
      "100-117": { single: [26, 28], quad: [18, 20] },
    },
    medium: {
      // 75-89kg
      "80-89": { single: [24, 26], quad: [16, 18] },
      "90-99": { single: [26, 28], quad: [18, 20] },
      "100-117": { single: [28, 30], quad: [20, 22] },
    },
    heavy: {
      // >= 90kg
      "80-89": { single: [26, 28], quad: [18, 20] },
      "90-99": { single: [28, 30], quad: [20, 22] },
      "100-117": { single: [30, 32], quad: [22, 24] },
    },
  };

  /**
   * Generic fallback matrices by discipline (from fin-selector-rules.json)
   * Used when no model-specific matrix is available
   * @constant {Object}
   */
  const GENERIC_FALLBACK = {
    slalom: {
      "50-55": { "5.0-5.5": 25, "5.5-6.0": 27, "6.0-6.5": 29 },
      "55-60": { "5.5-6.0": 29, "6.0-6.5": 31, "6.5-7.0": 33 },
      "60-65": { "6.0-6.5": 31, "6.5-7.0": 33, "7.0-7.5": 35 },
      "65-70": { "6.5-7.0": 35, "7.0-7.5": 37, "7.5-8.0": 39 },
      "70-75": { "7.0-7.5": 37, "7.5-8.0": 39, "8.0-8.5": 41, "8.5-9.0": 43 },
      "75-80": { "7.5-8.0": 41, "8.0-8.5": 43, "8.5-9.0": 45, "9.0-9.5": 47 },
      "80-85": { "8.0-8.5": 45, "8.5-9.0": 47, "9.0-9.5": 49, "9.5-10.0": 51 },
      "85-90": { "8.5-9.0": 49, "9.0-9.5": 51, "9.5-10.0": 53, "10.0+": 55 },
    },
    freeride: {
      "65-70": { "5.5-6.0": 34, "6.0-6.5": 36, "6.5-7.0": 38 },
      "70-75": { "6.0-6.5": 38, "6.5-7.0": 40, "7.0-7.5": 42 },
      "75-80": { "6.5-7.0": 42, "7.0-7.5": 44, "7.5-8.0": 46 },
      "80-85": { "7.0-7.5": 44, "7.5-8.0": 46, "8.0-8.5": 48 },
    },
    freerace: {
      "55-60": { "5.4-6.2": 31, "6.2-7.0": 33, "7.0-7.8": 35 },
      "60-65": { "5.4-6.2": 33, "6.2-7.0": 35, "7.0-7.8": 37 },
      "65-70": { "6.2-7.0": 37, "7.0-7.8": 39, "7.8-8.5": 41 },
      "70-75": { "7.0-7.8": 41, "7.8-8.5": 43, "8.5-9.5": 45 },
      "75-80": { "7.8-8.5": 43, "8.5-9.5": 47, "9.5+": 49 },
      "80-85": { "8.5-9.5": 47, "9.5+": 49 },
    },
    speed: {
      "45-50": { "4.6-5.4": 21, "5.4-6.0": 23 },
      "50-55": { "5.0-5.4": 25, "5.4-6.4": 27 },
      "55-60": { "5.4-6.4": 27, "6.4-7.0": 29 },
      "60-65": { "6.4-7.0": 29, "7.0-7.8": 31 },
      "65-70": { "7.0-7.8": 31, "7.8+": 33 },
    },
  };

  /**
   * Antiweed sail-to-size mapping (model-specific varies)
   * Generic fallback based on sail size
   * @constant {Object}
   */
  const ANTIWEED_SAIL_MAP = {
    4.0: 22,
    4.5: 24,
    5.0: 26,
    5.5: 28,
    6.0: 30,
    6.5: 32,
    7.0: 34,
    7.5: 36,
    8.0: 38,
    8.5: 40,
    9.0: 42,
  };

  /**
   * Box type compatibility per discipline
   * Defines which box types are valid for each discipline
   * @constant {Object}
   */
  const BOX_COMPATIBILITY = {
    wave: {
      tuttle: true,
      deep_tuttle: false,
      powerbox: true,
      us_box: true,
      slot_box: true,
      trim_box: false,
      mini_tuttle: true,
    },
    freestyle: { tuttle: true, deep_tuttle: false, powerbox: true, us_box: true, slot_box: true, trim_box: true },
    freeride: { tuttle: true, deep_tuttle: true, powerbox: true, us_box: true, slot_box: true, trim_box: true },
    freerace: { tuttle: true, deep_tuttle: true, powerbox: true, us_box: false, slot_box: false, trim_box: false },
    slalom: { tuttle: true, deep_tuttle: true, powerbox: true, us_box: false, slot_box: false, trim_box: false },
    speed: { tuttle: true, deep_tuttle: false, powerbox: false, us_box: false, slot_box: false, trim_box: false },
  };

  /**
   * Box type display labels
   * @constant {Object}
   */
  const BOX_LABELS = {
    tuttle: "Tuttle",
    deep_tuttle: "Deep Tuttle",
    powerbox: "Powerbox",
    us_box: "US Box",
    slot_box: "Slot Box",
    trim_box: "Trim Box",
  };

  /**
   * Discipline groups for flexible filtering
   * Maps primary discipline to compatible product disciplines
   * @constant {Object}
   */
  const DISCIPLINE_GROUPS = {
    slalom: ["slalom"],
    freerace: ["freerace", "slalom"],
    freeride: ["freeride", "freerace", "bump & jump", "bumpjump"],
    wave: ["wave"],
    freestyle: ["freestyle"],
    speed: ["speed", "slalom"],
  };

  // ============================================================================
  // EVENT NAMES
  // ============================================================================

  const EVENTS = {
    STATE_CHANGE: "fsv5:stateChange",
    STEP_CHANGE: "fsv5:stepChange",
    RESULTS_UPDATE: "fsv5:resultsUpdate",
    COMPARE_UPDATE: "fsv5:compareUpdate",
  };

  // ============================================================================
  // CORE STATE
  // ============================================================================

  /**
   * Vendor mapping by skill level
   * - recreational: Unifiber brand only
   * - advanced: Both Unifiber and Select brands
   * - race/competition: Select brand only
   * @constant {Object}
   */
  const SKILL_VENDOR_MAP = {
    recreational: ["Unifiber"],
    advanced: ["Unifiber"],
    race: ["Unifiber"],
    competition: ["Unifiber"],
  };

  /**
   * Creates a fresh state object
   * @returns {Object} Initial state
   */
  function createInitialState() {
    return {
      currentStep: 0,
      skillLevel: null, // 'recreational', 'advanced', 'race', 'competition'
      discipline: null, // 'wave', 'freestyle', 'freeride', 'freerace', 'slalom', 'speed'
      boxType: null, // 'powerbox', 'tuttle_box', etc.
      antiweed: false, // toggle
      sailSize: 7.0, // m2 (range 1.5-12.5)
      boardVolume: 130, // L (range 60-200)
      boardWidth: 70, // cm (range 50-100)
      riderWeight: 80, // kg (for wave/freestyle)
      recommendedSize: null, // calculated
      recommendedRange: { min: null, max: null },
      selectedForCompare: [], // max 3 product IDs
      products: [], // loaded products
      filteredProducts: [], // after filtering
    };
  }

  // ============================================================================
  // FIN SELECTOR V5 CLASS
  // ============================================================================

  /**
   * FinSelectorV5Core - Core logic for fin selector
   * @class
   */
  class FinSelectorV5Core {
    /**
     * Creates a new FinSelectorV5Core instance
     * @param {HTMLElement} containerEl - Container element
     * @param {Object} options - Configuration options
     */
    constructor(containerEl, options = {}) {
      this.container = containerEl;
      this.options = {
        tolerance: SIZE_TOLERANCE,
        maxCompare: MAX_COMPARE_ITEMS,
        onStateChange: null,
        onStepChange: null,
        onResultsUpdate: null,
        onCompareUpdate: null,
        ...options,
      };

      // Initialize state
      this.state = createInitialState();

      // State change listeners
      this._listeners = [];

      // Merge initial products if provided
      if (options.products && Array.isArray(options.products)) {
        this.state.products = options.products;
      }

      // Merge initial state values if provided
      if (options.initialState) {
        Object.assign(this.state, options.initialState);
      }
    }

    // ==========================================================================
    // STATE MANAGEMENT
    // ==========================================================================

    /**
     * Update a single state property
     * @param {string} key - State key to update
     * @param {*} value - New value
     * @returns {void}
     */
    updateState(key, value) {
      const oldValue = this.state[key];

      // Skip if value hasn't changed (shallow comparison)
      if (oldValue === value) {
        return;
      }

      // Update state
      this.state[key] = value;

      // Track if this is a step change
      const isStepChange = key === "currentStep";

      // Dispatch state change event
      this._dispatchEvent(EVENTS.STATE_CHANGE, {
        key,
        oldValue,
        newValue: value,
        state: this.getState(),
      });

      // Dispatch step change event if applicable
      if (isStepChange) {
        this._dispatchEvent(EVENTS.STEP_CHANGE, {
          previousStep: oldValue,
          currentStep: value,
          state: this.getState(),
        });
      }

      // Notify listeners
      this._notifyListeners(key, value, oldValue);

      // Call option callbacks
      if (this.options.onStateChange) {
        this.options.onStateChange(key, value, oldValue);
      }

      if (isStepChange && this.options.onStepChange) {
        this.options.onStepChange(oldValue, value);
      }
    }

    /**
     * Update multiple state properties at once
     * @param {Object} updates - Object with key-value pairs to update
     * @returns {void}
     */
    updateStateMultiple(updates) {
      const changes = [];

      Object.entries(updates).forEach(([key, value]) => {
        const oldValue = this.state[key];
        if (oldValue !== value) {
          this.state[key] = value;
          changes.push({ key, oldValue, newValue: value });
        }
      });

      // Only dispatch if there were actual changes
      if (changes.length > 0) {
        this._dispatchEvent(EVENTS.STATE_CHANGE, {
          changes,
          state: this.getState(),
        });

        // Check for step changes
        const stepChange = changes.find((c) => c.key === "currentStep");
        if (stepChange) {
          this._dispatchEvent(EVENTS.STEP_CHANGE, {
            previousStep: stepChange.oldValue,
            currentStep: stepChange.newValue,
            state: this.getState(),
          });
        }

        // Notify all listeners once
        changes.forEach(({ key, newValue, oldValue }) => {
          this._notifyListeners(key, newValue, oldValue);
        });
      }
    }

    /**
     * Get current state (immutable copy)
     * @returns {Object} Current state
     */
    getState() {
      return { ...this.state };
    }

    /**
     * Register a state change listener
     * @param {Function} callback - Callback function(key, newValue, oldValue)
     * @returns {Function} Unsubscribe function
     */
    onStateChange(callback) {
      if (typeof callback !== "function") {
        throw new Error("Callback must be a function");
      }

      this._listeners.push(callback);

      // Return unsubscribe function
      return () => {
        const index = this._listeners.indexOf(callback);
        if (index > -1) {
          this._listeners.splice(index, 1);
        }
      };
    }

    /**
     * Notify all registered listeners
     * @private
     */
    _notifyListeners(key, newValue, oldValue) {
      this._listeners.forEach((callback) => {
        try {
          callback(key, newValue, oldValue);
        } catch (error) {
          console.error("[FSV5] Listener error:", error);
        }
      });
    }

    /**
     * Dispatch custom event on container
     * @private
     */
    _dispatchEvent(eventName, detail) {
      if (!this.container) return;

      const event = new CustomEvent(eventName, {
        bubbles: true,
        cancelable: true,
        detail,
      });

      this.container.dispatchEvent(event);
    }

    // ==========================================================================
    // SIZING CALCULATIONS
    // ==========================================================================

    /**
     * Calculate recommended fin size based on current state
     * Uses matrix lookup from fin-selector-rules.json with fallback to generic formula
     * @param {Object} [stateOverride] - Optional state override for calculation
     * @returns {number} Recommended fin size in cm
     */
    calculateRecommendedSize(stateOverride = null) {
      const s = stateOverride || this.state;
      const discipline = (s.discipline || "freeride").toLowerCase();

      // Handle antiweed mode
      if (s.antiweed) {
        const size = this._calculateAntiweedSize(s.sailSize);
        this._updateSizeState(size);
        return size;
      }

      // Handle wave (volume-based)
      if (discipline === "wave") {
        const size = this._calculateWaveSize(s.boardVolume, s.riderWeight, s.finSetup || "single");
        this._updateSizeState(size);
        return size;
      }

      // Handle freestyle (volume-based, but capped at 25cm max)
      if (discipline === "freestyle") {
        const size = this._calculateFreestyleSize(s.boardVolume, s.riderWeight, s.finSetup || "single");
        this._updateSizeState(size);
        return size;
      }

      // Try matrix lookup first (slalom, freeride, freerace, speed)
      const matrixSize = this._lookupFromMatrix(discipline, s.boardWidth, s.sailSize);
      if (matrixSize !== null) {
        this._updateSizeState(matrixSize);
        return matrixSize;
      }

      // Fallback to generic formula: board_width/2 + discipline offset + sail influence
      const baseSize = s.boardWidth / 2;
      const disciplineOffset = DISCIPLINE_OFFSETS[discipline] || 1;
      const sailInfluence = (s.sailSize - 7.0) * 1.5;
      const recommendedSize = Math.round(baseSize + disciplineOffset + sailInfluence);

      this._updateSizeState(recommendedSize);
      return recommendedSize;
    }

    /**
     * Update state with calculated size and range
     * @private
     */
    _updateSizeState(size) {
      this.state.recommendedSize = size;
      this.state.recommendedRange = this.calculateRange(size);
    }

    /**
     * Lookup fin size from generic fallback matrix
     * @private
     * @param {string} discipline - Discipline name
     * @param {number} boardWidth - Board width in cm
     * @param {number} sailSize - Sail size in m²
     * @returns {number|null} Fin size or null if not found
     */
    _lookupFromMatrix(discipline, boardWidth, sailSize) {
      const matrix = GENERIC_FALLBACK[discipline];
      if (!matrix) return null;

      // Find matching board width range
      const widthRanges = Object.keys(matrix);
      let matchedWidthRange = null;

      for (const range of widthRanges) {
        const [min, max] = range.split("-").map(Number);
        if (boardWidth >= min && boardWidth < max) {
          matchedWidthRange = range;
          break;
        }
      }

      if (!matchedWidthRange) {
        // Try to find closest range
        const lastRange = widthRanges[widthRanges.length - 1];
        const [lastMin] = lastRange.split("-").map(Number);
        if (boardWidth >= lastMin) {
          matchedWidthRange = lastRange;
        }
      }

      if (!matchedWidthRange) return null;

      // Find matching sail size range
      const sailRanges = matrix[matchedWidthRange];
      if (!sailRanges) return null;

      for (const sailRange of Object.keys(sailRanges)) {
        if (sailRange.includes("+")) {
          // Handle "10.0+" style ranges
          const min = parseFloat(sailRange.replace("+", ""));
          if (sailSize >= min) {
            return sailRanges[sailRange];
          }
        } else {
          const [minSail, maxSail] = sailRange.split("-").map(parseFloat);
          if (sailSize >= minSail && sailSize < maxSail) {
            return sailRanges[sailRange];
          }
        }
      }

      // Return closest match if no exact match
      const sailKeys = Object.keys(sailRanges);
      if (sailKeys.length > 0) {
        // Find closest sail range
        let closestKey = sailKeys[0];
        let closestDiff = Infinity;

        for (const key of sailKeys) {
          const [minSail] = key.split("-").map(parseFloat);
          const diff = Math.abs(sailSize - minSail);
          if (diff < closestDiff) {
            closestDiff = diff;
            closestKey = key;
          }
        }
        return sailRanges[closestKey];
      }

      return null;
    }

    /**
     * Calculate size range with tolerance
     * @param {number} baseSize - Base fin size in cm
     * @returns {Object} Range object {min, max}
     */
    calculateRange(baseSize) {
      const tolerance = this.options.tolerance || SIZE_TOLERANCE;
      return {
        min: baseSize - tolerance,
        max: baseSize + tolerance,
      };
    }

    /**
     * Calculate freestyle fin size from volume matrix (capped at 25cm max)
     * Freestyle fins are more conservative than wave fins (typically 17-25cm)
     * Based on board specification tables showing 18-20cm for typical freestyle boards:
     * - Small boards (70-80L): 18cm
     * - Medium boards (80-100L): 19cm
     * - Large boards (100-110L): 20cm
     * @private
     * @param {number} boardVolume - Board volume in liters
     * @param {number} riderWeight - Rider weight in kg
     * @param {string} finSetup - Fin setup: 'single', 'quad', 'thruster'
     * @returns {number} Recommended fin size (capped at 25cm for freestyle)
     */
    _calculateFreestyleSize(boardVolume, riderWeight, finSetup = "single") {
      // Freestyle uses similar calculation to wave but with more conservative sizing
      // Typical freestyle range: 17-25cm (per fin-selector-rules.json)
      const waveSize = this._calculateWaveSize(boardVolume, riderWeight, finSetup);

      // Apply conservative adjustment: freestyle fins are typically 2-4cm smaller than wave
      // For practical freestyle use, cap at 22cm (gives 19-25cm range with ±3cm tolerance)
      // This ensures recommendations stay in the practical freestyle range
      // and matches real-world board specs showing 18-20cm fins
      if (waveSize > 22) {
        return 22; // Cap at 22cm for practical freestyle (range: 19-25cm)
      }

      // For smaller volumes, use wave calculation but ensure minimum practical size
      // Small boards (70-80L) typically use 18cm, medium (80-100L) use 19cm
      return Math.min(waveSize, 25);
    }

    /**
     * Calculate wave fin size from volume matrix
     * @private
     * @param {number} boardVolume - Board volume in liters
     * @param {number} riderWeight - Rider weight in kg
     * @param {string} finSetup - Fin setup: 'single', 'quad', 'thruster'
     * @returns {number} Recommended fin size (middle of range)
     */
    _calculateWaveSize(boardVolume, riderWeight, finSetup = "single") {
      const weightCat = this._getWeightCategory(riderWeight);
      const volumeRanges = Object.keys(WAVE_VOLUME_MATRIX[weightCat] || {});

      // Find matching volume range
      let matchedRange = null;
      for (const range of volumeRanges) {
        const [min, max] = range.split("-").map(Number);
        if (boardVolume >= min && boardVolume <= max) {
          matchedRange = range;
          break;
        }
      }

      // If no match, find closest range
      if (!matchedRange && volumeRanges.length > 0) {
        if (boardVolume < 80) matchedRange = "80-89";
        else if (boardVolume > 117) matchedRange = "100-117";
        else matchedRange = volumeRanges[0];
      }

      if (!matchedRange) {
        // Ultimate fallback
        return 26;
      }

      const setup = finSetup === "quad" ? "quad" : "single";
      const sizeRange = WAVE_VOLUME_MATRIX[weightCat][matchedRange][setup];

      if (Array.isArray(sizeRange) && sizeRange.length === 2) {
        // Return middle of range
        return Math.round((sizeRange[0] + sizeRange[1]) / 2);
      }

      return sizeRange || 26;
    }

    /**
     * Calculate antiweed fin size based on sail size
     * @private
     */
    _calculateAntiweedSize(sailSize) {
      const sails = Object.keys(ANTIWEED_SAIL_MAP)
        .map(Number)
        .sort((a, b) => a - b);
      let closestSail = sails[0];

      for (const s of sails) {
        if (Math.abs(s - sailSize) < Math.abs(closestSail - sailSize)) {
          closestSail = s;
        }
      }

      return ANTIWEED_SAIL_MAP[closestSail];
    }

    /**
     * Get weight category from kg
     * @private
     */
    _getWeightCategory(weight) {
      if (weight < 75) return "light";
      if (weight < 90) return "medium";
      return "heavy";
    }

    // ==========================================================================
    // BOX COMPATIBILITY
    // ==========================================================================

    /**
     * Get compatible box types for a discipline
     * @param {string} discipline - Discipline name
     * @returns {Array} Array of compatible box type objects {key, label, compatible}
     */
    getBoxCompatibility(discipline) {
      const normalizedDiscipline = (discipline || "").toLowerCase().replace(/[\s-_]/g, "");
      const compatibility = BOX_COMPATIBILITY[normalizedDiscipline] || BOX_COMPATIBILITY.freeride;

      return Object.entries(compatibility).map(([key, compatible]) => ({
        key,
        label: BOX_LABELS[key] || key,
        compatible,
      }));
    }

    /**
     * Get only compatible box types for a discipline
     * @param {string} discipline - Discipline name
     * @returns {Array} Array of compatible box type keys
     */
    getCompatibleBoxTypes(discipline) {
      return this.getBoxCompatibility(discipline)
        .filter((box) => box.compatible)
        .map((box) => box.key);
    }

    // ==========================================================================
    // PRODUCT FILTERING
    // ==========================================================================

    /**
     * Filter products based on current state
     * @param {Array} [products] - Products to filter (defaults to state.products)
     * @param {Object} [stateOverride] - Optional state override
     * @returns {Array} Filtered products
     */
    filterProducts(products = null, stateOverride = null) {
      const productList = products || this.state.products;
      const s = stateOverride || this.state;

      if (!productList || productList.length === 0) {
        return [];
      }

      // Calculate current recommended range if not set
      if (!s.recommendedRange || s.recommendedRange.min === null) {
        const size = this.calculateRecommendedSize(s);
        s.recommendedRange = this.calculateRange(size);
      }

      const filtered = productList.filter((product) => {
        // Exclude products in the exclusion list
        if (EXCLUDED_PRODUCT_HANDLES.includes(product.handle)) {
          return false;
        }

        // Filter by skill level (vendor mapping)
        // Special case: Freestyle discipline always includes Unifiber fins regardless of skill level
        const isFreestyleDiscipline = s.discipline && s.discipline.toLowerCase().replace(/[\s-_]/g, "") === "freestyle";
        const isUnifiberProduct = (product.vendor || "").toLowerCase().includes("unifiber");

        if (s.skillLevel && !isFreestyleDiscipline) {
          // Normal filtering for non-freestyle disciplines
          if (!this._matchesSkillVendor(product, s.skillLevel)) {
            return false;
          }
        } else if (s.skillLevel && isFreestyleDiscipline && !isUnifiberProduct) {
          // For freestyle: allow Unifiber always, but still filter Select by skill level
          if (!this._matchesSkillVendor(product, s.skillLevel)) {
            return false;
          }
        }
        // If freestyle + Unifiber, always allow (skip skill level filter)

        // Filter by box type
        if (s.boxType && !this._matchesBoxType(product, s.boxType)) {
          return false;
        }

        // Filter by antiweed OR discipline (antiweed overrides discipline filter)
        if (s.antiweed) {
          // When antiweed toggle is ON, show only antiweed products
          const productIsAntiweed = this._isAntiweedProduct(product);
          if (!productIsAntiweed) {
            return false;
          }
        } else {
          // When antiweed is OFF, filter by discipline (and exclude antiweed products)
          if (s.discipline && !this._matchesDiscipline(product, s.discipline)) {
            return false;
          }
          // Also exclude antiweed products from normal discipline results
          if (this._isAntiweedProduct(product)) {
            return false;
          }
        }

        // Filter by size range (skip if no fin_size data or if range is not set)
        const productSize = parseInt(product.fin_size) || 0;
        if (
          productSize > 0 &&
          s.recommendedRange &&
          s.recommendedRange.min !== null &&
          s.recommendedRange.max !== null
        ) {
          if (!this._matchesSizeRange(product, s.recommendedRange)) {
            return false;
          }
        }

        // NOTE: We intentionally do NOT filter by availability here.
        // Stock status is fetched live from Shopify and displayed visually on cards.
        // Users should see all matching products regardless of stock status so they can:
        // - Compare specifications
        // - Sign up for back-in-stock notifications
        // - See what options exist for their criteria
        // The Add to Cart button is disabled for out-of-stock items.

        return true;
      });

      // Update state and dispatch event
      this.state.filteredProducts = filtered;
      this._dispatchEvent(EVENTS.RESULTS_UPDATE, {
        count: filtered.length,
        products: filtered,
        state: this.getState(),
      });

      if (this.options.onResultsUpdate) {
        this.options.onResultsUpdate(filtered);
      }

      return filtered;
    }

    /**
     * Check if product matches skill level vendor requirements
     * @private
     */
    _matchesSkillVendor(product, skillLevel) {
      const allowedVendors = SKILL_VENDOR_MAP[skillLevel];
      if (!allowedVendors || allowedVendors.length === 0) {
        return true; // No vendor restriction
      }

      const productVendor = (product.vendor || "").toLowerCase().trim();
      return allowedVendors.some((vendor) => productVendor.includes(vendor.toLowerCase()));
    }

    /**
     * Check if product matches box type
     * @private
     */
    _matchesBoxType(product, selectedBox) {
      const boxTypeRaw = product.box_type;

      // Handle various types: string, array, object, null
      let productBox = "";
      if (typeof boxTypeRaw === "string") {
        productBox = boxTypeRaw;
      } else if (Array.isArray(boxTypeRaw)) {
        productBox = boxTypeRaw[0] || "";
      } else if (boxTypeRaw && typeof boxTypeRaw === "object") {
        // Handle metaobject reference
        productBox = boxTypeRaw.name || boxTypeRaw.value || String(boxTypeRaw) || "";
      }
      productBox = String(productBox).toLowerCase().trim();

      const normalizeBox = (box) => box.replace(/[\s_-]/g, "").toLowerCase();
      const normalizedProductBox = normalizeBox(productBox);
      const normalizedSelectedBox = normalizeBox(selectedBox);

      // Tuttle: exact match (not deep tuttle, not mini tuttle)
      if (selectedBox === "tuttle") {
        return (
          normalizedProductBox === "tuttle" ||
          (normalizedProductBox.includes("tuttle") &&
            !normalizedProductBox.includes("deep") &&
            !normalizedProductBox.includes("mini"))
        );
      }

      // Deep Tuttle: must contain "deep"
      if (selectedBox === "deep_tuttle") {
        return normalizedProductBox.includes("deep") && normalizedProductBox.includes("tuttle");
      }

      // Mini Tuttle: must contain "mini" and "tuttle"
      if (selectedBox === "mini_tuttle") {
        return normalizedProductBox.includes("mini") && normalizedProductBox.includes("tuttle");
      }

      // Other box types: flexible matching
      return (
        normalizedProductBox === normalizedSelectedBox ||
        normalizedProductBox.includes(normalizedSelectedBox) ||
        normalizedSelectedBox.includes(normalizedProductBox)
      );
    }

    /**
     * Check if product matches discipline
     * @private
     */
    _matchesDiscipline(product, selectedDiscipline) {
      // Handle various types for discipline
      let rawDiscipline = product.discipline;
      if (rawDiscipline && typeof rawDiscipline === "object") {
        rawDiscipline = rawDiscipline.name || rawDiscipline.value || "";
      }
      const productDiscipline = String(rawDiscipline || "")
        .toLowerCase()
        .replace(/[\s-_]/g, "");
      const normalizedSelected = String(selectedDiscipline || "")
        .toLowerCase()
        .replace(/[\s-_]/g, "");

      const allowedDisciplines = DISCIPLINE_GROUPS[normalizedSelected] || [normalizedSelected];

      return allowedDisciplines.some((d) => {
        const normalizedAllowed = d.replace(/[\s-_]/g, "");
        return productDiscipline.includes(normalizedAllowed) || normalizedAllowed.includes(productDiscipline);
      });
    }

    /**
     * Check if product is an antiweed fin
     * @private
     */
    _isAntiweedProduct(product) {
      const discipline = (product.discipline || "").toLowerCase();
      const title = (product.title || "").toLowerCase();
      const tags = (product.tags || []).map((t) => t.toLowerCase());

      return (
        discipline.includes("antiweed") ||
        discipline.includes("anti-weed") ||
        discipline.includes("weed") ||
        title.includes("weed") ||
        tags.some((t) => t.includes("weed") || t.includes("antiweed"))
      );
    }

    /**
     * Check if product size is within range
     * @private
     */
    _matchesSizeRange(product, range) {
      if (!range || range.min === null || range.max === null) {
        return true; // Skip size filtering if range is not set
      }
      const productSize = parseInt(product.fin_size) || 0;
      return productSize >= range.min && productSize <= range.max;
    }

    // ==========================================================================
    // SORTING
    // ==========================================================================

    /**
     * Sort products by relevance to recommended size
     * @param {Array} products - Products to sort
     * @param {number} [recommendedSize] - Target size (defaults to state)
     * @returns {Array} Sorted products (closest to recommended first)
     */
    sortByRelevance(products, recommendedSize = null) {
      const targetSize = recommendedSize || this.state.recommendedSize;

      if (!targetSize || !products || products.length === 0) {
        return products || [];
      }

      return [...products].sort((a, b) => {
        const sizeA = parseInt(a.fin_size) || 0;
        const sizeB = parseInt(b.fin_size) || 0;
        const diffA = Math.abs(sizeA - targetSize);
        const diffB = Math.abs(sizeB - targetSize);

        // Primary: closest to recommended size
        if (diffA !== diffB) {
          return diffA - diffB;
        }

        // Secondary: premium tier first
        const tierA = this._getTierPriority(a.construction_tier);
        const tierB = this._getTierPriority(b.construction_tier);
        if (tierA !== tierB) {
          return tierA - tierB;
        }

        // Tertiary: in stock first
        if (a.available !== b.available) {
          return a.available ? -1 : 1;
        }

        return 0;
      });
    }

    /**
     * Get tier priority for sorting
     * @private
     */
    _getTierPriority(tier) {
      const priorities = {
        premium: 0,
        performance: 1,
        value: 2,
      };
      return priorities[(tier || "").toLowerCase()] || 3;
    }

    // ==========================================================================
    // COMPARE FUNCTIONALITY
    // ==========================================================================

    /**
     * Add product to compare selection
     * @param {string|number} productId - Product ID to add
     * @returns {boolean} Success status
     */
    addToCompare(productId) {
      const id = String(productId);
      const current = this.state.selectedForCompare;

      // Already selected
      if (current.includes(id)) {
        return false;
      }

      // Max limit reached
      if (current.length >= this.options.maxCompare) {
        return false;
      }

      const updated = [...current, id];
      this.state.selectedForCompare = updated;

      this._dispatchEvent(EVENTS.COMPARE_UPDATE, {
        action: "add",
        productId: id,
        selectedIds: updated,
        count: updated.length,
      });

      if (this.options.onCompareUpdate) {
        this.options.onCompareUpdate("add", id, updated);
      }

      return true;
    }

    /**
     * Remove product from compare selection
     * @param {string|number} productId - Product ID to remove
     * @returns {boolean} Success status
     */
    removeFromCompare(productId) {
      const id = String(productId);
      const current = this.state.selectedForCompare;
      const index = current.indexOf(id);

      if (index === -1) {
        return false;
      }

      const updated = current.filter((pid) => pid !== id);
      this.state.selectedForCompare = updated;

      this._dispatchEvent(EVENTS.COMPARE_UPDATE, {
        action: "remove",
        productId: id,
        selectedIds: updated,
        count: updated.length,
      });

      if (this.options.onCompareUpdate) {
        this.options.onCompareUpdate("remove", id, updated);
      }

      return true;
    }

    /**
     * Toggle product in compare selection
     * @param {string|number} productId - Product ID to toggle
     * @returns {boolean} Whether product is now selected
     */
    toggleCompare(productId) {
      const id = String(productId);
      const isSelected = this.state.selectedForCompare.includes(id);

      if (isSelected) {
        this.removeFromCompare(id);
        return false;
      } else {
        return this.addToCompare(id);
      }
    }

    /**
     * Clear all compare selections
     * @returns {void}
     */
    clearCompare() {
      const previous = [...this.state.selectedForCompare];
      this.state.selectedForCompare = [];

      this._dispatchEvent(EVENTS.COMPARE_UPDATE, {
        action: "clear",
        previousIds: previous,
        selectedIds: [],
        count: 0,
      });

      if (this.options.onCompareUpdate) {
        this.options.onCompareUpdate("clear", null, []);
      }
    }

    /**
     * Check if product is selected for compare
     * @param {string|number} productId - Product ID to check
     * @returns {boolean}
     */
    isSelectedForCompare(productId) {
      return this.state.selectedForCompare.includes(String(productId));
    }

    /**
     * Get products selected for compare
     * @returns {Array} Selected products
     */
    getCompareProducts() {
      const selectedIds = this.state.selectedForCompare;
      return this.state.products.filter((p) => selectedIds.includes(String(p.id)));
    }

    // ==========================================================================
    // STEP NAVIGATION
    // ==========================================================================

    /**
     * Go to next step
     * @returns {number} New step index
     */
    nextStep() {
      const newStep = this.state.currentStep + 1;
      this.updateState("currentStep", newStep);
      return newStep;
    }

    /**
     * Go to previous step
     * @returns {number} New step index
     */
    prevStep() {
      const newStep = Math.max(0, this.state.currentStep - 1);
      this.updateState("currentStep", newStep);
      return newStep;
    }

    /**
     * Go to specific step
     * @param {number} step - Step index
     * @returns {number} New step index
     */
    goToStep(step) {
      const newStep = Math.max(0, step);
      this.updateState("currentStep", newStep);
      return newStep;
    }

    // ==========================================================================
    // UTILITY METHODS
    // ==========================================================================

    /**
     * Reset state to initial values
     * @returns {void}
     */
    reset() {
      const initialState = createInitialState();

      // Preserve products if loaded
      if (this.state.products.length > 0) {
        initialState.products = this.state.products;
      }

      this.state = initialState;
      this._dispatchEvent(EVENTS.STATE_CHANGE, {
        action: "reset",
        state: this.getState(),
      });
    }

    /**
     * Load products into state
     * @param {Array} products - Products array
     * @returns {void}
     */
    loadProducts(products) {
      this.state.products = products || [];
      this._dispatchEvent(EVENTS.STATE_CHANGE, {
        key: "products",
        state: this.getState(),
      });
    }

    /**
     * Get discipline label
     * @param {string} discipline - Discipline key
     * @returns {string} Display label
     */
    getDisciplineLabel(discipline) {
      const labels = {
        wave: "Wave",
        freestyle: "Freestyle",
        freeride: "Freeride",
        freerace: "Freerace",
        slalom: "Slalom",
        speed: "Speed",
      };
      return labels[discipline] || discipline;
    }

    /**
     * Get box type label
     * @param {string} boxType - Box type key
     * @returns {string} Display label
     */
    getBoxTypeLabel(boxType) {
      return BOX_LABELS[boxType] || boxType;
    }

    /**
     * Destroy instance and clean up
     * @returns {void}
     */
    destroy() {
      this._listeners = [];
      this.state = createInitialState();
      this.container = null;
    }
  }

  // ============================================================================
  // FACTORY FUNCTION
  // ============================================================================

  /**
   * Initialize a new FinSelectorV5 instance
   * @param {HTMLElement} containerEl - Container element
   * @param {Object} options - Configuration options
   * @returns {FinSelectorV5Core} Instance
   */
  function initFinSelectorV5(containerEl, options = {}) {
    if (!containerEl) {
      throw new Error("[FSV5] Container element is required");
    }

    return new FinSelectorV5Core(containerEl, options);
  }

  // ============================================================================
  // STATIC UTILITY FUNCTIONS (for use without instance)
  // ============================================================================

  /**
   * Calculate recommended size (static version)
   * @param {Object} params - Calculation parameters
   * @returns {number} Recommended fin size
   */
  function calculateRecommendedSize(params) {
    const {
      discipline = "freeride",
      boardWidth = 70,
      sailSize = 7.0,
      boardVolume = 130,
      riderWeight = 80,
      antiweed = false,
    } = params;

    // Antiweed mode
    if (antiweed) {
      const sails = Object.keys(ANTIWEED_SAIL_MAP)
        .map(Number)
        .sort((a, b) => a - b);
      let closestSail = sails[0];
      for (const s of sails) {
        if (Math.abs(s - sailSize) < Math.abs(closestSail - sailSize)) {
          closestSail = s;
        }
      }
      return ANTIWEED_SAIL_MAP[closestSail];
    }

    // Volume-based (wave/freestyle)
    if (discipline === "wave" || discipline === "freestyle") {
      const weightCat = riderWeight < 75 ? "light" : riderWeight < 90 ? "medium" : "heavy";
      const volumes = Object.keys(VOLUME_WEIGHT_MATRIX)
        .map(Number)
        .sort((a, b) => a - b);
      let closestVolume = volumes[0];
      for (const v of volumes) {
        if (Math.abs(v - boardVolume) < Math.abs(closestVolume - boardVolume)) {
          closestVolume = v;
        }
      }
      return VOLUME_WEIGHT_MATRIX[closestVolume][weightCat];
    }

    // Standard formula
    const baseSize = boardWidth / 2;
    const disciplineOffset = DISCIPLINE_OFFSETS[discipline] || 1;
    const sailInfluence = (sailSize - 7.0) * 1.5;

    return Math.round(baseSize + disciplineOffset + sailInfluence);
  }

  /**
   * Calculate size range (static version)
   * @param {number} baseSize - Base fin size
   * @param {number} [tolerance=3] - Tolerance in cm
   * @returns {Object} Range {min, max}
   */
  function calculateRange(baseSize, tolerance = SIZE_TOLERANCE) {
    return {
      min: baseSize - tolerance,
      max: baseSize + tolerance,
    };
  }

  /**
   * Get box compatibility for discipline (static version)
   * @param {string} discipline - Discipline name
   * @returns {Object} Compatibility map
   */
  function getBoxCompatibility(discipline) {
    const normalized = (discipline || "").toLowerCase().replace(/[\s-_]/g, "");
    return BOX_COMPATIBILITY[normalized] || BOX_COMPATIBILITY.freeride;
  }

  // ============================================================================
  // EXPORT
  // ============================================================================

  // Expose to global namespace
  global.FinSelectorV5 = {
    // Factory
    init: initFinSelectorV5,

    // Class (for advanced use)
    Core: FinSelectorV5Core,

    // Static utilities
    calculateRecommendedSize: calculateRecommendedSize,
    calculateRange: calculateRange,
    getBoxCompatibility: getBoxCompatibility,

    // Constants
    EVENTS: EVENTS,
    SIZE_TOLERANCE: SIZE_TOLERANCE,
    MAX_COMPARE_ITEMS: MAX_COMPARE_ITEMS,
    DISCIPLINE_OFFSETS: DISCIPLINE_OFFSETS,
    BOX_COMPATIBILITY: BOX_COMPATIBILITY,
    BOX_LABELS: BOX_LABELS,

    // Version
    VERSION: "5.0.0",
  };
})(typeof window !== "undefined" ? window : this);